 
export interface GetOrderplanResponse {
  orderplanByPeriods: OrderplanPeriodData[];
  orderplanByCropAids: OrderplanProductData[];
}

export interface OrderplanPeriodData {
  periodId: number;
  orderplanRequisiteSummaries: OrderplanRequisiteSummary[];
  manuallyAddedProducts: ManuallyAddedCropAid[];
}

export interface OrderplanRequisiteSummary {
  cropAidId: number;
  customerProductInventoryUsedAmount: number;
  orderLinesTotalAmount: number;
  usedOrderOverflowAmount: number;
  requisiteAmount: number;
}

export interface ManuallyAddedCropAid {
  cropAidId: number;
  orderLinesTotalAmount: number;
}

export interface OrderplanPeriodDisplayData {
  period: SalesOrderPeriod;
  orderplanRequisiteSummaries: OrderplanRequisiteDisplaySummary[];
  manuallyAddedCropAids: ManuallyAddedCropAidDisplay[];
}

export interface OrderplanRequisiteDisplaySummary {
  cropAid: CropAid;
  orderLines: OrderLine[];
  customerProductInventoryUsedAmount: number;
  orderLinesTotalAmount: number;
  usedOrderOverflowAmount: number;
  requisiteAmount: number;
}

export interface ManuallyAddedCropAidDisplay {
  cropAid: CropAid;
  orderLines: OrderLine[];
  orderLinesTotalAmount: number;
}

export interface OrderplanProductData {
  cropAidId: number;
  customerProductInventoryAmount: number;
  orderLinesTotalAmount: number;
  requisiteAmount: number;
}

export interface OrderplanCropAidDisplayData {
  cropAid: CropAid;
  customerProductInventoryAmount: number;
  orderLinesTotalAmount: number;
  requisiteAmount: number;
}

export interface GetOrderLinesResponse {
  orderLines: OrderLine[];
}

export interface OrderLine {
  id: number;
  packageId: number;
  cropAidId?: number;
  count: number;
  year: number;
  periodId: number;
  status: OrderLineStatus;
  deliveryPeriodMoment: DeliveryPeriodMomentType;
  treatmentType: TreatmentTypes;
  orderedDate: string;
  package?: Package;
  cropAid?: CropAid;
  shipToId: number;
  addedByOptimizer: boolean;
  /**
   * Newest version (2022) of tracking orderlines to the cart-service.
   */
  cartId?: number;
  /**
   * Old version (~2020-2021) of tracking orderlines to the checkout-service.
   */
  checkoutId?: number;
  /**
   * Oldest version (~2020) of tracking orderlines to the order-management-service.
   */
  orderedId?: number;
}

export interface RapportOrderLine extends OrderLine {
  cropAid?: CropAid;
  package?: Package;
}

export interface OrderPeriod {
  periodInfo: SalesOrderPeriod;
  orderLines: OrderLine[];
}

export interface ReportOrderLine {
  cropAid?: CropAid;
  package?: Package;
  count: number;
  requisiteAmount?: number;
}

export interface OrderReport {
  orderedDate: string;
  status?: OrderLineStatus;
  periodInfo: SalesOrderPeriod;
  orderLines: ReportOrderLine[];

  /**
   * Newest version (2022) of tracking orderlines to the cart-service.
   */
  cartId?: number;
  /**
   * Old version (~2020-2021) of tracking orderlines to the checkout-service.
   */
  checkoutId?: number;
  /**
   * Oldest version (~2020) of tracking orderlines to the order-management-service.
   */
  orderedId?: number;
}

export enum OrderLineStatus {
  open = 'Open',
  inCart = 'InCart',
  finalized = 'Finalized',
}

export enum DeliveryPeriodMomentType {
  empty = 'Empty',
  beginning = 'Beginning',
  middle = 'Middle',
  end = 'End',
}

export enum TreatmentTypes {
  GBM = 'GBM',
  Fertilization = 'Fertilization'
}

export interface DeliveryPeriodMoment {
  periodId: number;
  deliveryPeriodMoment: DeliveryPeriodMomentType;
}

export interface DeliveryPeriodMomentDisplay {
  period: SalesOrderPeriod;
  deliveryPeriodMoment: DeliveryPeriodMomentType;
}

export interface SetDeliveryPeriodMomentsRequest {
  year: number;
  deliveryPeriodMoments: DeliveryPeriodMoment[];
}

export interface UpsertCustomerProductInventoryRequest {
  year: number;
  productInventories: ProductInventoryData[];
}

export interface ProductInventoryData {
  cropAidId: number;
  amount: number;
}

export enum SourceEnum {
  Manual = 'Manual',
  AgrifirmArableLegacySAP = 'AgrifirmArableLegacySAP',
  OneSAP = 'OneSAP',
}

export interface CropAid {
  id: number;
  productId: number;
  name: string;
  unitOfMeasurement: string;
  codes: string[];
  isActive: boolean;
  source: SourceEnum;
}

export interface GetPackagesResponse {
  packages: Package[];
}

export interface Package {
  id: number;
  unitOfMeasurement: string;
  productId: number;
  amount: number;
  isActive: boolean;
  name: string;
  packageVersion: PackageVersion;
}

export enum PackageVersion {
  V1 = 'V1',
  V2 = 'V2',
}
export interface GetSalesOrderPeriodsResponse {
  salesOrderPeriods: SalesOrderPeriod[];
}

export interface SalesOrderPeriod {
  id: number;
  periodNumber: number;
  name: string;
  orderReceivedBefore: string | Date;
  startDispatchFrom: string | Date;
  deliverBefore: string | Date;
  year?: number;
  periodType: PeriodSystem;
}

export interface UpsertOrderLineRequest extends OrderLine {
  year: number;
  cropAidId: number;
}

export interface MultipleSelectResult {
  keyValues: KeyValue[];
}

export interface KeyValue {
  key: string;
  value: string;
}

export interface CreateOrderRequest {
  year: number;
  salesOrderPeriodIds: number[];
}

export interface CreateOrderResponse {
  id: number;
}

export interface UpsertPreferedDeliveryPeriodsRequest {
  year: number;
  preferedDeliveryPeriods: number[];
}

export interface UpsertNumberOfPeriodsRequest {
  year: number;
  periodSystem: string;
}

export interface GetCustomerPreferencesResponse {
  customerId: number;
  year: number;
  preferedDeliveryPeriods: number[];
  periodSystem: PeriodSystem;
}

export interface PreferredDeliveryPeriods {
  year: number;
  periods: number[];
}

export interface OrderLineDialogResult extends OrderLine {
  cropAidId: number;
}

export interface SelectLocationDialogResult {
  locationId: number;
}

export enum PeriodSystem {
  GBM = 'GBM',
  Horticulture = 'Horticulture',
}

export interface GetCropAidsResponse {
  cropAids: CropAidResponse[];
}

export interface CropAidResponse {
  id: number;
  productId: number;
  productV2Id?: number;
  name: string;
  isActive: boolean;
  unitOfMeasurement: string;
  cropProtection: CropProtectionData;
  source: SourceEnum;
}

export interface CropProtectionData {
  admissions: Admission[];
}

export interface Admission {
  id: number;
  number: string;
}
